import { country, address } from '@prisma/client';
import { produce, original } from 'immer';
import { useState, useEffect } from 'react';
import * as yup from 'yup';
import { fetchWrapper } from '@/com_lib/request';
import { AddressPack } from '../../pages/api/account';
import { Typography, FormControlLabel, Checkbox } from '@mui/material';
import { CheckboxBlackSxProps } from '../Common';
import SingleAddress from './SingleAddress';
import { TFunction } from 'next-i18next';
import { formikSetValue } from '@/com_lib/formik_helper';
import { CheckoutInfo, FlatternAddress } from '@/com_lib/common';

export const validationShippingAddressSchemaFunc = (t: (input: string) => string) => yup.object({
	ship_firstName: yup
		.string()
		.required(t('名字是必需的')),
	ship_lastName: yup
		.string()
		.required(t('姓氏是必需的')),
	ship_phone: yup
		.string()
		.required(t('電話號碼是必需的')),
	ship_address: yup
		.string()
		.required(t('地址是必需的')),
	ship_city: yup
		.string()
		.required(t('城市是必需的')),
	ship_province: yup
		.string()
		.required(t('省/州是必需的')),
	ship_country: yup
		.string()
		.required(t('國家是必需的')),
	ship_postCode: yup
		.string()
		.required(t('郵政編碼是必需的')),
});

export const validationShippingAndBillingAddressSchemaFunc = (t: (input: string) => string) => yup.object({
	ship_firstName: yup
		.string()
		.required(t('名字是必需的')),
	ship_lastName: yup
		.string()
		.required(t('姓氏是必需的')),
	ship_phone: yup
		.string()
		.required(t('電話號碼是必需的')),
	ship_address: yup
		.string()
		.required(t('地址是必需的')),
	ship_city: yup
		.string()
		.required(t('城市是必需的')),
	ship_province: yup
		.string()
		.required(t('省/州是必需的')),
	ship_country: yup
		.string()
		.required(t('國家是必需的')),
	ship_postCode: yup
		.string()
		.required(t('郵政編碼是必需的')),

	bill_firstName: yup
		.string()
		.required(t('名字是必需的')),
	bill_lastName: yup
		.string()
		.required(t('姓氏是必需的')),
	bill_phone: yup
		.string()
		.required(t('電話號碼是必需的')),
	bill_address: yup
		.string()
		.required(t('地址是必需的')),
	bill_city: yup
		.string()
		.required(t('城市是必需的')),
	bill_province: yup
		.string()
		.required(t('省/州是必需的')),
	bill_country: yup
		.string()
		.required(t('國家是必需的')),
	bill_postCode: yup
		.string()
		.required(t('郵政編碼是必需的')),
});

export function useGetCountries() {
	const [countryDataReady, set_countryDataReady] = useState(false);
	const [countries, set_countries] = useState<country[]>(null);

	useEffect(() => {
		let run = true;

		const getCountries = async () => {
			const data = {
				action: 'get_countries'
			};
			const countries = await fetchWrapper.post("/api/account", data) as country[];
			if (!run)
				return;

			set_countries(countries);
		}

		Promise.all([getCountries()]).then(() => {
			if (run)
				set_countryDataReady(true);
		});

		return () => { run = false };

	}, []);

	return [countryDataReady, countries] as const;
}

export function useAddressCommon(t: TFunction, language: string, checkoutInfo?: CheckoutInfo,
	callByAdminEditUser?: boolean, accountId?: number,
) {
	const [_addressDataReady, set_dataReady] = useState(false);
	// If checkoutInfo is passed and checkoutInfo.addressData is set, then rawAddressData is always null.
	const [rawAddressData, set_rawData] = useState<AddressPack>(null);
	// If checkoutInfo is passed and checkoutInfo.addressData is set, below 2 variables come from checkoutInfo.
	const [currAddressData, set_currData] = useState<FlatternAddress>(null);
	const [billingSameAsShippingAddress, set_billingSameAsShippingAddress] = useState(true);

	const [countryDataReady, countries] = useGetCountries();

	useEffect(() => {
		if (checkoutInfo && checkoutInfo.addressData) {
			set_currData(checkoutInfo.addressData);
			set_billingSameAsShippingAddress(checkoutInfo.billingSameAsShippingAddress);

			set_dataReady(true);
		}
		else {
			let run = true;

			const getUserAddress = async () => {
				const data = {
					action: callByAdminEditUser ? 'admin_get_user_addresses' : 'get_user_addresses',
					userId: accountId
				};
				const addressPack = await fetchWrapper.post(callByAdminEditUser ? "/api/admin/account" : "/api/account", data) as AddressPack;
				if (!run)
					return;

				const ship = addressPack.ship;
				const bill = addressPack.bill;

				const newData: FlatternAddress = {
					ship_id: ship?.id,
					ship_firstName: ship?.firstName,
					ship_lastName: ship?.lastName,
					ship_phone: ship?.phone,
					ship_address: ship?.address,
					ship_city: ship?.city,
					ship_province: ship?.province,
					ship_country: ship?.country,
					ship_postCode: ship?.postCode,

					bill_id: bill?.id,
					bill_firstName: bill?.firstName,
					bill_lastName: bill?.lastName,
					bill_phone: bill?.phone,
					bill_address: bill?.address,
					bill_city: bill?.city,
					bill_province: bill?.province,
					bill_country: bill?.country,
					bill_postCode: bill?.postCode,
				}

				set_rawData(addressPack);
				set_currData(newData);
				set_billingSameAsShippingAddress(addressPack.billingSameAsShippingAddress);
			}

			Promise.all([getUserAddress()]).then(() => {
				if (run)
					set_dataReady(true);
			});

			return () => { run = false };
		}

	}, [checkoutInfo]);

	const addressComponentsRdr = (formik: any) => <>
		<>
			<Typography sx={{ fontSize: "24px", mb: "20px" }}>
				{t('送貨地址')}
			</Typography>
			<SingleAddress t={t} language={language} formik={formik} fieldPrefix="ship_" countries={countries} />
			<Typography sx={{ fontSize: "24px", mt: "20px" }}>
				{t('帳單地址')}
			</Typography>
			<FormControlLabel
				sx={{ alignSelf: "self-start" }}
				control={
					<Checkbox sx={{ ...CheckboxBlackSxProps, my: "8px" }}
						checked={billingSameAsShippingAddress}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							const newValue = event.target.checked;
							set_billingSameAsShippingAddress(newValue);
						}}
					/>
				}
				label={
					<Typography
						sx={{
							fontSize: "16px",
							a: {
								textDecoration: "underline !important"
							}
						}}
					>
						{t("與送貨地址相同")}
					</Typography>
				}
			/>
			{!billingSameAsShippingAddress &&
				<SingleAddress t={t} language={language} formik={formik} fieldPrefix="bill_" countries={countries} />
			}
		</>
	</>

	const addressDataReady = _addressDataReady && countryDataReady;
	return [addressDataReady, rawAddressData, currAddressData, billingSameAsShippingAddress, addressComponentsRdr] as const;
}

export function useFillFakeAddress(formik: any, billingSameAsShippingAddress: boolean) {
//////////////
//////////////////
//////////////////////////////////////////
//////////////////////
//////////////////////////////////////////////////////////////
////////////////////////////////////////////////////
//////////////////////////////////////////////////////
///////////////////////////////////////////////////////////
/////////////////////////////////////////////////////
//////////////////////////////////////////////////
/////////////////////////////////////////////////
///////////////////////////////////////////////////////

////////////////////////////////////////
///////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////
///////////////////////////////////////////////////////
////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////
///////////////////////////////////////////////////
//////////////////////////////////////////////////
////////////////////////////////////////////////////////
/////

///////////////////////////////////////////////////////
////
///

///////////////////////////////////////////////
///////////////////////////////////////////////////////////////
////////////////////////////////////
///////////
}

export function patchAddressPack(rawData: AddressPack, values: FlatternAddress, billingSameAsShippingAddress: boolean) {
	const newAddressPack = produce(rawData, draft => {
		const old = original(draft);
		if (!old.ship)
			draft.ship = {} as address;

		if (!old.bill)
			draft.bill = {} as address;

		draft.ship.id = values.ship_id;
		draft.ship.firstName = values.ship_firstName;
		draft.ship.lastName = values.ship_lastName;
		draft.ship.phone = values.ship_phone;
		draft.ship.address = values.ship_address;
		draft.ship.city = values.ship_city;
		draft.ship.province = values.ship_province;
		draft.ship.country = values.ship_country;
		draft.ship.postCode = values.ship_postCode;

		draft.bill.id = values.bill_id;
		draft.bill.firstName = values.bill_firstName;
		draft.bill.lastName = values.bill_lastName;
		draft.bill.phone = values.bill_phone;
		draft.bill.address = values.bill_address;
		draft.bill.city = values.bill_city;
		draft.bill.province = values.bill_province;
		draft.bill.country = values.bill_country;
		draft.bill.postCode = values.bill_postCode;

		draft.billingSameAsShippingAddress = billingSameAsShippingAddress;
	});

	return newAddressPack;
}

